import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Paper, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Grid, Alert, Typography, Chip, InputAdornment, Tooltip,
  FormControl, InputLabel, Select, Stack, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Add, Edit, Search, Delete, CalendarMonth, Visibility, Group,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import PageHeader from '../common/PageHeader';

const STATUS_OPTIONS = [
  { v: 'open',                  label: 'Open',                  color: 'default' },
  { v: 'researching',           label: 'Researching',           color: 'info' },
  { v: 'suppliers_identified',  label: 'Suppliers Identified',  color: 'primary' },
  { v: 'quotes_received',       label: 'Quotes Received',       color: 'warning' },
  { v: 'supplier_selected',     label: 'Supplier Selected',     color: 'success' },
  { v: 'closed',                label: 'Closed',                color: 'secondary' },
  { v: 'cancelled',             label: 'Cancelled',             color: 'error' },
];
const PRIORITY_OPTIONS = [
  { v: 'urgent', label: 'Urgent', color: 'error' },
  { v: 'high',   label: 'High',   color: 'warning' },
  { v: 'medium', label: 'Medium', color: 'primary' },
  { v: 'low',    label: 'Low',    color: 'default' },
];
const CURRENCIES = ['USD', 'EUR', 'GBP', 'CNY', 'HKD'];
const UOM = ['pcs','kg','g','m','roll','box','set'];

const statusChip = (s) => {
  const o = STATUS_OPTIONS.find(x => x.v === s);
  return <Chip size="small" label={o?.label || s} color={o?.color || 'default'} variant={o?.color === 'default' ? 'outlined' : 'filled'} />;
};
const priorityChip = (s) => {
  const o = PRIORITY_OPTIONS.find(x => x.v === s);
  return <Chip size="small" label={o?.label?.toUpperCase() || s} color={o?.color || 'default'} />;
};

// Quantity formatter — integer with thousands separator, drops trailing zeros
const qty = (v) => {
  if (v == null || v === '') return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return Number.isInteger(n)
    ? n.toLocaleString('en-US')
    : n.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const EMPTY = {
  product_name: '', category: '', description: '', specifications: '',
  target_quantity: '', unit_of_measure: 'pcs', target_unit_price: '', target_currency: 'USD',
  required_by: '', priority: 'medium', status: 'open', notes: '',
};

export default function ProductSearches() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [list, setList]                       = useState([]);
  const [stats, setStats]                     = useState(null);
  const [search, setSearch]                   = useState('');
  const [filterStatus, setFilterStatus]       = useState('');
  const [filterPriority, setFilterPriority]   = useState('');
  const [filterOverdue, setFilterOverdue]     = useState(false);
  const [dialog, setDialog]                   = useState(false);
  const [editing, setEditing]                 = useState(null);
  const [form, setForm]                       = useState(EMPTY);
  const [error, setError]                     = useState('');
  const [loading, setLoading]                 = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search)         params.append('search', search);
      if (filterStatus)   params.append('status', filterStatus);
      if (filterPriority) params.append('priority', filterPriority);
      if (filterOverdue)  params.append('overdue', 'true');
      const r = await api.get(`/sourcing-requests?${params}`);
      setList(r.data.data || []);
      const s = await api.get('/sourcing-requests/stats');
      setStats(s.data);
    } catch (e) { console.error(e); }
  }, [search, filterStatus, filterPriority, filterOverdue]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || !list.length) return;
    const found = list.find(r => String(r.id) === String(editId));
    if (found) {
      openEdit(found);
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    }

  }, [list]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setError(''); setDialog(true); };
  const openEdit   = (r) => {
    setEditing(r);
    const cleanQty   = (v) => (v == null || v === '') ? '' : String(Math.round(parseFloat(v)));
    const cleanPrice = (v) => (v == null || v === '') ? '' : String(parseFloat(v));
    setForm({
      ...EMPTY, ...r,
      target_quantity:   cleanQty(r.target_quantity),
      target_unit_price: cleanPrice(r.target_unit_price),
      required_by:       r.required_by ? r.required_by.substring(0, 10) : '',
    });
    setError(''); setDialog(true);
  };

  const handleSave = async () => {
    if (!form.product_name) { setError('Product name is required'); return; }
    setLoading(true);
    try {
      const payload = { ...form };
      ['target_quantity','target_unit_price'].forEach(k => {
        if (payload[k] === '' || payload[k] == null) payload[k] = null;
        else payload[k] = parseFloat(payload[k]);
      });
      if (editing) await api.put(`/sourcing-requests/${editing.id}`, payload);
      else         await api.post('/sourcing-requests', payload);
      setDialog(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving');
    } finally { setLoading(false); }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete "${r.product_name}"?`)) return;
    await api.delete(`/sourcing-requests/${r.id}`);
    load();
  };

  const f = (field) => ({ value: form[field] ?? '', onChange: (e) => setForm({ ...form, [field]: e.target.value }) });

  return (
    <Box>
      <PageHeader title="Product Searches"
        subtitle="Track products you're sourcing — even before you've found suppliers"
        action={<Button variant="contained" startIcon={<Add />} onClick={openCreate}>New Search</Button>}
      />

      {/* Summary */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} md={3}>
            <Card><CardContent sx={{ py: 1.75 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>Total Searches</Typography>
              <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>{(stats.by_status || []).reduce((s, x) => s + x.c, 0)}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ borderColor: stats.overdue ? 'error.main' : undefined }}><CardContent sx={{ py: 1.75 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>Overdue</Typography>
              <Typography variant="h5" fontWeight={700} color={stats.overdue ? 'error.main' : undefined} sx={{ mt: 0.5 }}>{stats.overdue}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card><CardContent sx={{ py: 1.75 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>Active by Status</Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {(stats.by_status || []).filter(s => !['closed','cancelled'].includes(s.status)).map(s =>
                  <Chip key={s.status} size="small"
                    label={`${STATUS_OPTIONS.find(x => x.v === s.status)?.label || s.status}: ${s.c}`}
                    color={STATUS_OPTIONS.find(x => x.v === s.status)?.color || 'default'}
                    variant="outlined" />
                )}
              </Stack>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField size="small" placeholder="Search product, category, ref…" value={search} onChange={e => setSearch(e.target.value)} sx={{ flex: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {STATUS_OPTIONS.map(s => <MenuItem key={s.v} value={s.v}>{s.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Priority</InputLabel>
          <Select value={filterPriority} label="Priority" onChange={e => setFilterPriority(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {PRIORITY_OPTIONS.map(s => <MenuItem key={s.v} value={s.v}>{s.label}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant={filterOverdue ? 'contained' : 'outlined'} color="error" size="small"
          startIcon={<CalendarMonth />} onClick={() => setFilterOverdue(!filterOverdue)}>
          Overdue Only
        </Button>
      </Stack>

      {isMobile ? (
        list.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            No active searches. Tap "New Search" to start.
          </Paper>
        ) : (
          <Stack spacing={1.25}>
            {list.map(r => {
              const overdue = r.days_to_deadline != null && r.days_to_deadline < 0 && !['closed','cancelled','supplier_selected'].includes(r.status);
              return (
                <Paper key={r.id} elevation={0}
                  onClick={() => navigate(`/sourcing/searches/${r.id}`)}
                  sx={{
                    p: 1.75, border: '1px solid',
                    borderColor: overdue ? 'error.light' : 'divider',
                    borderLeft: '4px solid',
                    borderLeftColor: overdue ? 'error.main' :
                      r.priority === 'urgent' ? 'error.main' :
                      r.priority === 'high'   ? 'warning.main' :
                      r.priority === 'medium' ? 'primary.main' : 'grey.300',
                    cursor: 'pointer', touchAction: 'manipulation',
                    '&:active': { bgcolor: 'action.hover' },
                  }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', display: 'block', fontSize: 11 }}>{r.request_ref}</Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.product_name}</Typography>
                      {r.category && <Typography variant="caption" color="text.secondary">{r.category}</Typography>}
                    </Box>
                    {statusChip(r.status)}
                  </Box>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ gap: 0.75, mb: 0.75 }}>
                    {priorityChip(r.priority)}
                    <Chip icon={<Group sx={{ fontSize: 14 }} />} label={`${r.candidate_count} candidate${r.candidate_count !== 1 ? 's' : ''}`}
                      size="small" color={r.candidate_count > 0 ? 'primary' : 'default'} variant="outlined" />
                    {r.required_by && (
                      <Chip size="small" variant="outlined"
                        color={overdue ? 'error' : r.days_to_deadline < 7 ? 'warning' : 'default'}
                        label={r.days_to_deadline != null && !['closed','cancelled','supplier_selected'].includes(r.status)
                          ? (r.days_to_deadline < 0 ? `${Math.abs(r.days_to_deadline)}d overdue` : `${r.days_to_deadline}d left`)
                          : r.required_by.substring(0, 10)} />
                    )}
                  </Stack>
                  {(r.target_quantity || r.target_unit_price) && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, fontSize: 12 }}>
                      <Box><Typography variant="caption" color="text.secondary">Target Qty</Typography>
                        <Typography variant="body2">{qty(r.target_quantity)} {r.unit_of_measure || ''}</Typography></Box>
                      <Box><Typography variant="caption" color="text.secondary">Target Price</Typography>
                        <Typography variant="body2">{r.target_unit_price ? `${r.target_currency} ${r.target_unit_price}` : '—'}</Typography></Box>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 0.25 }} onClick={e => e.stopPropagation()}>
                    <IconButton size="small" onClick={() => openEdit(r)}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(r)}><Delete fontSize="small" /></IconButton>
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        )
      ) : (
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Ref</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="right">Target Qty</TableCell>
              <TableCell align="right">Target Price</TableCell>
              <TableCell align="center">Candidates</TableCell>
              <TableCell>Required By</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.length === 0 ? (
              <TableRow><TableCell colSpan={10} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                No active searches. Create one to start tracking a product you're sourcing.
              </TableCell></TableRow>
            ) : list.map(r => (
              <TableRow key={r.id} hover sx={{ bgcolor: r.days_to_deadline != null && r.days_to_deadline < 0 && !['closed','cancelled','supplier_selected'].includes(r.status) ? 'error.50' : undefined, cursor: 'pointer' }}
                onClick={() => navigate(`/sourcing/searches/${r.id}`)}>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.request_ref}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{r.product_name}</Typography>
                  {r.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{r.category || '—'}</TableCell>
                <TableCell align="right">{qty(r.target_quantity)} {r.unit_of_measure || ''}</TableCell>
                <TableCell align="right">{r.target_unit_price ? `${r.target_currency} ${r.target_unit_price}` : '—'}</TableCell>
                <TableCell align="center">
                  <Chip icon={<Group sx={{ fontSize: 14 }} />} label={r.candidate_count}
                    size="small" color={r.candidate_count > 0 ? 'primary' : 'default'} variant="outlined" />
                </TableCell>
                <TableCell>
                  {r.required_by ? (
                    <Box>
                      <Typography variant="body2" fontSize={12}>{r.required_by.substring(0, 10)}</Typography>
                      {r.days_to_deadline != null && !['closed','cancelled','supplier_selected'].includes(r.status) && (
                        <Typography variant="caption" color={r.days_to_deadline < 0 ? 'error.main' : r.days_to_deadline < 7 ? 'warning.main' : 'text.secondary'}>
                          {r.days_to_deadline < 0 ? `${Math.abs(r.days_to_deadline)}d overdue` : `${r.days_to_deadline}d left`}
                        </Typography>
                      )}
                    </Box>
                  ) : '—'}
                </TableCell>
                <TableCell>{priorityChip(r.priority)}</TableCell>
                <TableCell>{statusChip(r.status)}</TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="View Details"><IconButton size="small" color="info" onClick={() => navigate(`/sourcing/searches/${r.id}`)}><Visibility fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><Edit fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(r)}><Delete fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialog}
        onClose={(_, reason) => { if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') setDialog(false); }}
        fullWidth maxWidth="md">
        <DialogTitle>{editing ? `Edit Search ${editing.request_ref}` : 'New Product Search'}</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}><TextField fullWidth required label="Product Name" placeholder="e.g. RFID label 50x30mm UHF" {...f('product_name')} /></Grid>
            <Grid item xs={6} md={4}>
              <TextField select fullWidth label="Priority" {...f('priority')}>
                {PRIORITY_OPTIONS.map(o => <MenuItem key={o.v} value={o.v}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Category" placeholder="RFID / NFC / Smart Card / Reader" {...f('category')} /></Grid>
            <Grid item xs={6} md={3}>
              <TextField select fullWidth label="Status" {...f('status')}>
                {STATUS_OPTIONS.map(s => <MenuItem key={s.v} value={s.v}>{s.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} md={3}><TextField fullWidth type="date" label="Required By" InputLabelProps={{ shrink: true }} {...f('required_by')} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Description" placeholder="What is this product? Why are you sourcing it?" {...f('description')} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={3} label="Specifications" placeholder="Frequency, size, material, IC chip, color, packaging…" {...f('specifications')} /></Grid>
            <Grid item xs={4}><TextField fullWidth type="number" label="Target Quantity" {...f('target_quantity')} /></Grid>
            <Grid item xs={4}>
              <TextField select fullWidth label="UOM" {...f('unit_of_measure')}>
                {UOM.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={4}><TextField fullWidth type="number" label="Target Unit Price" {...f('target_unit_price')} /></Grid>
            <Grid item xs={6} md={4}>
              <TextField select fullWidth label="Currency" {...f('target_currency')}>
                {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Notes" {...f('notes')} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={loading}>{editing ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
